export type DocType =
  | 'Email'
  | 'Contract'
  | 'Memo'
  | 'Invoice'
  | 'Letter'
  | 'Report'
export type DocStatus =
  | 'Initial Review'
  | 'Enriched'
  | 'QA Flagged'
  | 'Privileged'

export interface ReviewDocument {
  id: string
  date: string
  type: DocType
  sender: string
  subject: string
  summary: string
  shortSummary?: string
  mediumSummary?: string
  fullSummary?: string
  status: DocStatus
  confidence: 'High' | 'Medium' | 'Low'
  threadId?: string
  isTerminal?: boolean
  threadPosition?: number
}

export interface Comment {
  id: string
  author: string
  initials: string
  text: string
  timestamp: string
  isCurrentUser?: boolean
}

// ── Thread A: Supply chain delays (4 emails) ──────────────────────────
// ── Thread B: Invoice dispute (3 emails) ──────────────────────────────
// ── Thread C: Project Phoenix (3 emails) ──────────────────────────────

export const MOCK_DOCUMENTS: ReviewDocument[] = [
  // ── Thread A: Supply chain delays ───────────────────────────────────
  {
    id: 'ACME-00001',
    date: '2023-07-10',
    type: 'Email',
    sender: 'j.smith@acme.com',
    subject: 'Q3 Supply Chain Concerns',
    summary:
      'Initial email raising concerns about Q3 supply chain delays affecting production timelines.',
    shortSummary:
      'Raises Q3 supply chain delay concerns impacting production schedules and vendor commitments.',
    mediumSummary:
      'J. Smith flags significant Q3 supply chain disruptions affecting three key production lines. Email details delayed shipments from Global Logistics, missed delivery windows for components sourced from Southeast Asia, and requests urgent review of alternative vendors. Attaches preliminary cost impact analysis estimating $2.1M in potential overruns.',
    fullSummary:
      'This email initiates the supply chain concerns thread by documenting delays across three production lines tied to Global Logistics shipments from Southeast Asia. Smith outlines specific missed delivery windows for Q3 component orders and flags a cascading risk to downstream assembly schedules, estimating $2.1M in potential cost overruns. The email requests an urgent vendor review meeting and attaches a preliminary cost impact analysis for leadership consideration.',
    status: 'Enriched',
    confidence: 'High',
    threadId: 'thread-1',
    threadPosition: 1,
  },
  {
    id: 'ACME-00002',
    date: '2023-07-12',
    type: 'Email',
    sender: 'r.patel@globallogistics.com',
    subject: 'Re: Q3 Supply Chain Concerns',
    summary:
      'Response from vendor acknowledging delays and proposing alternative shipping routes via EU.',
    status: 'Enriched',
    confidence: 'High',
    threadId: 'thread-1',
    threadPosition: 2,
  },
  {
    id: 'ACME-00003',
    date: '2023-07-14',
    type: 'Email',
    sender: 'j.smith@acme.com',
    subject: 'Re: Re: Q3 Supply Chain Concerns',
    summary:
      'Internal discussion of vendor proposal with cost analysis and risk assessment attached.',
    status: 'Enriched',
    confidence: 'Medium',
    threadId: 'thread-1',
    threadPosition: 3,
  },
  {
    id: 'ACME-00004',
    date: '2023-07-18',
    type: 'Email',
    sender: 'j.smith@acme.com',
    subject: 'Fwd: Q3 Supply Chain — Final Decision',
    summary:
      'Forward to leadership summarizing all prior chain emails and final vendor selection decision.',
    status: 'Enriched',
    confidence: 'High',
    threadId: 'thread-1',
    threadPosition: 4,
    isTerminal: true,
  },

  // ── Thread B: Invoice dispute ───────────────────────────────────────
  {
    id: 'ACME-00008',
    date: '2023-08-20',
    type: 'Email',
    sender: 'billing@techholdings.com',
    subject: 'Invoice #4492 — Q3 Consulting Services',
    summary:
      'Original invoice submission for $142,500 covering Q3 consulting and advisory services.',
    status: 'Initial Review',
    confidence: 'High',
    threadId: 'thread-2',
    threadPosition: 1,
  },
  {
    id: 'ACME-00009',
    date: '2023-08-22',
    type: 'Email',
    sender: 'finance@acme.com',
    subject: 'Re: Invoice #4492 — Disputing Hours',
    summary:
      'Finance team disputes 38 hours billed in August; requests itemized breakdown by consultant.',
    shortSummary:
      'Finance disputes 38 hours on Invoice #4492; requests itemized consultant breakdown.',
    mediumSummary:
      'Acme finance team formally disputes 38 hours billed on Invoice #4492 for August consulting services. The email identifies discrepancies between the SOW cap of 120 hours/month and the 158 hours invoiced, flags two consultants with overlapping time entries on Aug 14-16, and requests a full itemized breakdown by consultant with supporting timesheets before any payment is authorized.',
    fullSummary:
      'The Acme finance team raises a formal dispute over 38 hours billed on Invoice #4492, noting that the 158 total hours invoiced for August exceed the SOW-mandated cap of 120 hours per month. The email specifically identifies two consultants with overlapping time entries on August 14-16 and questions whether senior partner hours were billed at the correct rate tier. Finance requests a complete itemized breakdown by individual consultant with supporting timesheets before authorizing any payment on the invoice.',
    status: 'QA Flagged',
    confidence: 'High',
    threadId: 'thread-2',
    threadPosition: 2,
  },
  {
    id: 'ACME-00010',
    date: '2023-08-25',
    type: 'Email',
    sender: 'billing@techholdings.com',
    subject: 'Re: Re: Invoice #4492 — Revised Breakdown',
    summary:
      'Vendor provides itemized hours with revised total of $128,750 and supporting timesheets.',
    status: 'Enriched',
    confidence: 'High',
    threadId: 'thread-2',
    threadPosition: 3,
    isTerminal: true,
  },

  // ── Thread C: Project Phoenix ───────────────────────────────────────
  {
    id: 'ACME-00015',
    date: '2023-09-05',
    type: 'Email',
    sender: 'j.smith@acme.com',
    subject: 'Project Phoenix — Timeline Update',
    summary:
      'Confidential update on Project Phoenix restructuring timeline and key milestone changes.',
    status: 'Privileged',
    confidence: 'Low',
    threadId: 'thread-3',
    threadPosition: 1,
  },
  {
    id: 'ACME-00016',
    date: '2023-09-08',
    type: 'Email',
    sender: 'm.jones@acme.com',
    subject: 'Re: Project Phoenix — Timeline Update',
    summary:
      'Legal review of proposed timeline; flags potential regulatory filing deadline conflicts.',
    status: 'Privileged',
    confidence: 'Medium',
    threadId: 'thread-3',
    threadPosition: 2,
  },
  {
    id: 'ACME-00017',
    date: '2023-09-12',
    type: 'Email',
    sender: 'j.smith@acme.com',
    subject: 'Fwd: Project Phoenix — Revised Timeline & Approvals',
    summary:
      'Final consolidated timeline with all approvals, regulatory notes, and board sign-off memo.',
    status: 'Privileged',
    confidence: 'High',
    threadId: 'thread-3',
    threadPosition: 3,
    isTerminal: true,
  },

  // ── Standalone documents ────────────────────────────────────────────
  {
    id: 'ACME-00005',
    date: '2023-07-20',
    type: 'Contract',
    sender: 'legal@acme.com',
    subject: 'MSA — Acme Corp & Global Logistics Inc.',
    summary:
      'Master Service Agreement governing cloud infrastructure and logistics support services.',
    shortSummary:
      'MSA for cloud infrastructure and logistics support between Acme and Global Logistics.',
    mediumSummary:
      'Master Service Agreement establishing the commercial relationship between Acme Corp and Global Logistics Inc. for cloud infrastructure hosting and end-to-end logistics support. Covers a 36-month term with auto-renewal, defines SLA tiers for uptime (99.95%) and delivery windows, and includes liability caps at $5M per incident. Contains mutual indemnification and a broad force majeure clause.',
    fullSummary:
      'This MSA governs the full commercial relationship between Acme Corp and Global Logistics Inc., covering cloud infrastructure hosting and logistics fulfillment services over a 36-month initial term with automatic annual renewal. Key provisions include tiered SLA commitments (99.95% uptime, 48-hour delivery windows), liability caps at $5M per incident, and mutual indemnification clauses. The agreement also contains a notably broad force majeure provision that may be relevant to the supply chain delay claims in this matter.',
    status: 'Initial Review',
    confidence: 'High',
  },
  {
    id: 'ACME-00006',
    date: '2023-07-25',
    type: 'Memo',
    sender: 'ceo@acme.com',
    subject: 'European Division Restructuring',
    summary:
      'Internal memo detailing restructuring of EU division including headcount and budget changes.',
    status: 'Enriched',
    confidence: 'High',
  },
  {
    id: 'ACME-00007',
    date: '2023-08-01',
    type: 'Invoice',
    sender: 'accounts@smithassociates.com',
    subject: 'Legal Fees — July 2023',
    summary:
      'Monthly legal services invoice covering discovery preparation and deposition scheduling.',
    status: 'Initial Review',
    confidence: 'High',
  },
  {
    id: 'ACME-00011',
    date: '2023-08-28',
    type: 'Letter',
    sender: 'legal@acme.com',
    subject: 'Notice of Breach — Horizon Logistics',
    summary:
      'Formal breach of contract notice sent to Horizon Logistics regarding delivery SLA violations.',
    status: 'Enriched',
    confidence: 'High',
  },
  {
    id: 'ACME-00012',
    date: '2023-08-30',
    type: 'Contract',
    sender: 'legal@acme.com',
    subject: 'NDA — Prospective Buyer (Redacted)',
    summary:
      'Non-disclosure agreement for prospective acquisition buyer; contains redacted party names.',
    status: 'Enriched',
    confidence: 'High',
  },
  {
    id: 'ACME-00013',
    date: '2023-09-01',
    type: 'Report',
    sender: 'compliance@acme.com',
    subject: 'Q3 Compliance Audit Summary',
    summary:
      'Quarterly compliance audit findings covering data privacy, financial controls, and HR policy.',
    status: 'Initial Review',
    confidence: 'High',
  },
  {
    id: 'ACME-00014',
    date: '2023-09-03',
    type: 'Memo',
    sender: 'hr@acme.com',
    subject: 'Q4 Strategic Goals & Hiring Freeze',
    summary:
      'HR memo announcing Q4 strategic priorities and company-wide hiring freeze effective Oct 1.',
    status: 'Initial Review',
    confidence: 'High',
  },
  {
    id: 'ACME-00018',
    date: '2023-09-15',
    type: 'Email',
    sender: 'ceo@acme.com',
    subject: 'Hiring Freeze Exception — Sr. Engineer',
    summary:
      'CEO approves exception to hiring freeze for critical senior engineering role in platform team.',
    status: 'Enriched',
    confidence: 'High',
  },
  {
    id: 'ACME-00019',
    date: '2023-09-18',
    type: 'Invoice',
    sender: 'accounts@smithassociates.com',
    subject: 'Legal Fees — August 2023',
    summary:
      'Monthly invoice for August legal services including document review and witness preparation.',
    status: 'Initial Review',
    confidence: 'High',
  },
  {
    id: 'ACME-00020',
    date: '2023-09-20',
    type: 'Contract',
    sender: 'legal@acme.com',
    subject: 'Amendment #3 — Global Logistics MSA',
    summary:
      'Third amendment to MSA adjusting payment terms, SLA metrics, and force majeure provisions.',
    status: 'QA Flagged',
    confidence: 'Medium',
  },
  {
    id: 'ACME-00021',
    date: '2023-09-22',
    type: 'Report',
    sender: 'forensics@kpmg.com',
    subject: 'Forensic Accounting — Preliminary Findings',
    summary:
      'Preliminary forensic analysis of financial disclosures; identifies three areas of concern.',
    status: 'Enriched',
    confidence: 'High',
  },
  {
    id: 'ACME-00022',
    date: '2023-09-25',
    type: 'Email',
    sender: 'outside.counsel@lawfirm.com',
    subject: 'Deposition Prep — Former CFO',
    summary:
      'Outside counsel memo on deposition preparation strategy for former CFO testimony next month.',
    shortSummary:
      'Outside counsel deposition prep strategy memo for former CFO testimony next month.',
    mediumSummary:
      'Outside counsel outlines a three-phase deposition preparation strategy for the former CFO scheduled to testify next month. Covers document familiarization sessions focusing on FY2022 financial disclosures, mock examination rehearsals targeting anticipated opposing counsel lines of questioning, and a privilege boundary briefing to prevent inadvertent waiver. Recommends two full prep days minimum.',
    fullSummary:
      'This privileged attorney work product memo details a three-phase deposition preparation strategy for the former CFO, whose testimony is scheduled for next month. Phase one covers intensive document familiarization sessions focused on FY2022 financial disclosures and the forensic accounting findings. Phase two involves mock examination rehearsals targeting the five most likely opposing counsel lines of questioning, including questions about the approval chain for disputed transactions. Phase three is a privilege boundary briefing to ensure the witness avoids inadvertent waiver of attorney-client privilege during testimony.',
    status: 'Privileged',
    confidence: 'High',
  },
  {
    id: 'ACME-00023',
    date: '2023-09-28',
    type: 'Memo',
    sender: 'legal@acme.com',
    subject: 'Litigation Hold — Updated Custodian List',
    summary:
      'Updated litigation hold notice expanding custodian list to include three additional employees.',
    status: 'Enriched',
    confidence: 'High',
  },
  {
    id: 'ACME-00024',
    date: '2023-10-01',
    type: 'Letter',
    sender: 'opposing.counsel@defense.com',
    subject: 'Discovery Extension Request',
    summary:
      'Opposing counsel requests 30-day extension for discovery deadline citing document volume.',
    status: 'Initial Review',
    confidence: 'High',
  },
  {
    id: 'ACME-00025',
    date: '2023-10-03',
    type: 'Email',
    sender: 'legal@acme.com',
    subject: 'Re: Discovery Extension Request',
    summary:
      'Internal discussion on whether to agree to or oppose the requested discovery extension.',
    status: 'Initial Review',
    confidence: 'High',
  },
  {
    id: 'ACME-00026',
    date: '2023-10-05',
    type: 'Contract',
    sender: 'procurement@acme.com',
    subject: 'Vendor Agreement — DataSecure Inc.',
    summary:
      'New vendor agreement for encrypted document storage and chain-of-custody tracking system.',
    status: 'Initial Review',
    confidence: 'High',
  },
  {
    id: 'ACME-00027',
    date: '2023-10-08',
    type: 'Report',
    sender: 'forensics@kpmg.com',
    subject: 'Forensic Accounting — Final Report',
    summary:
      'Final forensic report with detailed findings on financial irregularities in FY2022 filings.',
    status: 'Enriched',
    confidence: 'High',
  },
  {
    id: 'ACME-00028',
    date: '2023-10-10',
    type: 'Invoice',
    sender: 'forensics@kpmg.com',
    subject: 'Forensic Analysis Services — Final Invoice',
    summary:
      'Final invoice for forensic accounting engagement totaling $287,000 across three work phases.',
    status: 'Initial Review',
    confidence: 'High',
  },
  {
    id: 'ACME-00029',
    date: '2023-10-12',
    type: 'Memo',
    sender: 'legal@acme.com',
    subject: 'Privilege Log — Draft v2',
    summary:
      'Second draft of privilege log covering 847 documents identified as potentially privileged.',
    status: 'QA Flagged',
    confidence: 'Medium',
  },
  {
    id: 'ACME-00030',
    date: '2023-10-15',
    type: 'Email',
    sender: 'partner@smithfirm.com',
    subject: 'Case Strategy — Confidential',
    summary:
      'Partner-level case strategy discussion covering settlement posture and trial preparation.',
    status: 'Privileged',
    confidence: 'High',
  },
  {
    id: 'ACME-00031',
    date: '2023-10-18',
    type: 'Letter',
    sender: 'legal@acme.com',
    subject: 'Response to Discovery Extension',
    summary:
      'Formal response agreeing to 15-day extension with conditions on rolling production schedule.',
    status: 'Enriched',
    confidence: 'High',
  },
  {
    id: 'ACME-00032',
    date: '2023-10-20',
    type: 'Report',
    sender: 'it@acme.com',
    subject: 'ESI Processing Summary — Batch 3',
    summary:
      'Processing report for third batch of ESI: 12,400 files processed, 847 flagged for review.',
    status: 'Enriched',
    confidence: 'High',
  },
  {
    id: 'ACME-00033',
    date: '2023-10-22',
    type: 'Contract',
    sender: 'legal@acme.com',
    subject: 'Settlement Framework — Draft',
    summary:
      'Draft settlement framework outlining proposed terms, payment structure, and release language.',
    status: 'Privileged',
    confidence: 'High',
  },
  {
    id: 'ACME-00034',
    date: '2023-10-25',
    type: 'Email',
    sender: 'm.jones@acme.com',
    subject: 'Document Production — Batch 3 Issues',
    summary:
      'Flags formatting issues in Batch 3 production set; 23 PDFs have corrupted metadata fields.',
    status: 'QA Flagged',
    confidence: 'Medium',
  },
  {
    id: 'ACME-00035',
    date: '2023-10-28',
    type: 'Invoice',
    sender: 'accounts@smithassociates.com',
    subject: 'Legal Fees — September 2023',
    summary:
      'September invoice for legal services: 342 hours billed across four attorneys and two paras.',
    status: 'Initial Review',
    confidence: 'High',
  },
  {
    id: 'ACME-00036',
    date: '2023-11-01',
    type: 'Memo',
    sender: 'legal@acme.com',
    subject: 'Deposition Summary — J. Williams',
    summary:
      'Summary of key testimony from J. Williams deposition including admissions on document access.',
    status: 'Enriched',
    confidence: 'High',
  },
  {
    id: 'ACME-00037',
    date: '2023-11-05',
    type: 'Email',
    sender: 'outside.counsel@lawfirm.com',
    subject: 'Expert Witness — Engagement Letter',
    summary:
      'Proposed engagement terms for forensic technology expert witness at $650/hr for trial prep.',
    status: 'Initial Review',
    confidence: 'High',
  },
  {
    id: 'ACME-00038',
    date: '2023-11-08',
    type: 'Report',
    sender: 'compliance@acme.com',
    subject: 'Data Privacy Impact Assessment',
    summary:
      'Assessment of data privacy implications for cross-border document transfers in discovery.',
    status: 'Enriched',
    confidence: 'High',
  },
  {
    id: 'ACME-00039',
    date: '2023-11-10',
    type: 'Letter',
    sender: 'court@nycourts.gov',
    subject: 'Scheduling Order — Amended',
    summary:
      'Amended court scheduling order setting new discovery cutoff and pre-trial conference dates.',
    status: 'Initial Review',
    confidence: 'High',
  },
  {
    id: 'ACME-00040',
    date: '2023-11-12',
    type: 'Contract',
    sender: 'legal@acme.com',
    subject: 'Tolling Agreement — Horizon Logistics',
    summary:
      'Tolling agreement extending statute of limitations for related claims by 180 days from signing.',
    status: 'Initial Review',
    confidence: 'High',
  },
  {
    id: 'ACME-00041',
    date: '2023-11-15',
    type: 'Email',
    sender: 'paralegal@smithfirm.com',
    subject: 'Privilege Review — Batch 4 Complete',
    summary:
      'Paralegal confirms completion of privilege review for Batch 4; 92 documents flagged for atty.',
    status: 'Enriched',
    confidence: 'High',
  },
  {
    id: 'ACME-00042',
    date: '2023-11-18',
    type: 'Memo',
    sender: 'legal@acme.com',
    subject: 'Motion to Compel — Draft Brief',
    summary:
      'Draft brief supporting motion to compel production of withheld financial records from 2021.',
    status: 'QA Flagged',
    confidence: 'Medium',
  },
  {
    id: 'ACME-00043',
    date: '2023-11-20',
    type: 'Invoice',
    sender: 'forensics@kpmg.com',
    subject: 'Supplemental Analysis — Invoice',
    summary:
      'Supplemental invoice for additional forensic analysis requested after deposition testimony.',
    status: 'Initial Review',
    confidence: 'High',
  },
  {
    id: 'ACME-00044',
    date: '2023-11-22',
    type: 'Report',
    sender: 'it@acme.com',
    subject: 'ESI Processing Summary — Batch 4',
    summary:
      'Final ESI processing batch: 8,200 files processed with 99.2% successful extraction rate.',
    status: 'Enriched',
    confidence: 'High',
  },
  {
    id: 'ACME-00045',
    date: '2023-11-25',
    type: 'Email',
    sender: 'partner@smithfirm.com',
    subject: 'Trial Preparation — Next Steps',
    summary:
      'Partner outlines next steps for trial preparation including witness order and exhibit list.',
    status: 'Privileged',
    confidence: 'High',
  },
]

export const INITIAL_COMMENTS: Record<string, Comment[]> = {
  'ACME-00001': [
    {
      id: 'c1',
      author: 'Sarah Chen',
      initials: 'SC',
      text: 'This is the start of the supply chain thread. Flagged for privilege review — mentions outside counsel in paragraph 3.',
      timestamp: '2023-10-20 09:14',
      isCurrentUser: false,
    },
    {
      id: 'c2',
      author: 'You',
      initials: 'JD',
      text: 'Agreed. Marking as potentially privileged. Will escalate to partner.',
      timestamp: '2023-10-20 11:32',
      isCurrentUser: true,
    },
  ],
  'ACME-00009': [
    {
      id: 'c3',
      author: 'Michael Torres',
      initials: 'MT',
      text: "QA flag: The disputed hours don't match the original SOW. Need to cross-reference with Exhibit 14.",
      timestamp: '2023-10-22 14:05',
      isCurrentUser: false,
    },
    {
      id: 'c4',
      author: 'Sarah Chen',
      initials: 'SC',
      text: 'Confirmed — SOW caps consulting at 120 hrs/month. Invoice shows 158 hrs for August.',
      timestamp: '2023-10-22 16:48',
      isCurrentUser: false,
    },
    {
      id: 'c5',
      author: 'You',
      initials: 'JD',
      text: "Good catch. Let's keep the QA flag and add this to the dispute summary memo.",
      timestamp: '2023-10-23 08:20',
      isCurrentUser: true,
    },
  ],
  'ACME-00015': [
    {
      id: 'c6',
      author: 'Sarah Chen',
      initials: 'SC',
      text: 'Low confidence score — AI summary may be inaccurate. Needs manual review of the full document.',
      timestamp: '2023-10-24 10:15',
      isCurrentUser: false,
    },
  ],
  'ACME-00022': [
    {
      id: 'c7',
      author: 'Michael Torres',
      initials: 'MT',
      text: 'Clearly privileged — attorney work product. Tagging for privilege log.',
      timestamp: '2023-10-25 13:40',
      isCurrentUser: false,
    },
    {
      id: 'c8',
      author: 'You',
      initials: 'JD',
      text: 'Confirmed. Added to privilege log draft v2.',
      timestamp: '2023-10-25 15:10',
      isCurrentUser: true,
    },
  ],
  'ACME-00029': [
    {
      id: 'c9',
      author: 'Sarah Chen',
      initials: 'SC',
      text: 'QA flag: Three entries in the privilege log are missing Bates range end numbers. Please verify.',
      timestamp: '2023-10-26 09:30',
      isCurrentUser: false,
    },
  ],
}
